import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { JsonLd } from "@/components/site/JsonLd";
import { buildPageStructuredData } from "@/data/schema";
import { useLang } from "@/i18n/LangContext";

const readHeadMeta = (lang: "sv" | "en") => {
  const title = document.title || "Intygsläkaren";
  const description =
    document.querySelector('meta[name="description"]')?.getAttribute("content") ||
    (lang === "sv"
      ? "Läkarintyg och medicinska utredningar i Göteborg."
      : "Medical certificates and assessments in Gothenburg.");
  return { title, description };
};

/**
 * Injects route-aware JSON-LD (MedicalClinic, WebSite, WebPage, breadcrumbs,
 * Service / Offer / FAQ / Article / HowTo / OfferCatalog where applicable).
 *
 * Page components set document.title / meta description in their own effects —
 * we re-sync via rAF, short timeouts, and a MutationObserver so crawlers get
 * the final title/description after client navigation.
 */
export const SiteSchema = () => {
  const { pathname } = useLocation();
  const { lang } = useLang();
  const [data, setData] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    let cancelled = false;
    let lastKey = "";

    const sync = async () => {
      const { title, description } = readHeadMeta(lang);
      const key = `${pathname}|${lang}|${title}|${description}`;
      if (key === lastKey) return;
      lastKey = key;
      const next = await buildPageStructuredData({ pathname, lang, title, description });
      if (!cancelled) setData(next);
    };

    void sync();
    const raf = window.requestAnimationFrame(() => {
      void sync();
    });
    const t1 = window.setTimeout(() => {
      void sync();
    }, 0);
    const t2 = window.setTimeout(() => {
      void sync();
    }, 120);

    const observer = new MutationObserver(() => {
      void sync();
    });
    observer.observe(document.head, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["content"],
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      observer.disconnect();
    };
  }, [lang, pathname]);

  return <JsonLd data={data} />;
};
